import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Card, Input, Modal, Form, 
  notification, Tooltip, Popconfirm, Tag, Transfer, Divider,
  Switch, Tabs, Alert
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, 
  ExclamationCircleOutlined, SearchOutlined, ReloadOutlined,
  SettingOutlined, LockOutlined,
  AlertFilled
} from '@ant-design/icons';
import roleService from '../../../services/roleService';
import permissionService from '../../../services/permissionService';
import userRoleUtils from '../../../utils/userRoleUtils';
import MyTable from '../../../components/common/MyTable';

const { Search } = Input;
const { confirm } = Modal;
const { TabPane } = Tabs;

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [adminPermissions, setAdminPermissions] = useState([]);
  const [regularPermissions, setRegularPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [form] = Form.useForm();
  const [tabActiveKey, setTabActiveKey] = useState('all');

  // Check if user has permission to manage roles
  const canManageRoles = userRoleUtils.hasPermission('manage_roles');

  // Load roles and permissions on component mount
  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  // Filter roles based on search text and tab
  const filteredRoles = roles.filter(role => {
    const matchesSearch = 
      role.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchText.toLowerCase());
    
    if (tabActiveKey === 'all') {
      return matchesSearch;
    } else if (tabActiveKey === 'admin') {
      return matchesSearch && role.is_admin === 1;
    } else if (tabActiveKey === 'regular') {
      return matchesSearch && role.is_admin !== 1;
    }
    
    return matchesSearch;
  });

  // Fetch roles from API
  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.getAllRoles();
      setRoles(response.data.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching roles',
        description: error.message || 'Failed to load roles'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch permissions from API
  const fetchPermissions = async () => {
    try {
      // Fetch all permissions for reference
      const allResponse = await permissionService.getAllPermissions();
      setPermissions(allResponse.data.data || []);
      
      // Separate admin and regular permissions
      const adminPerms = (allResponse.data.data || []).filter(p => p.is_admin === 1);
      const regularPerms = (allResponse.data.data || []).filter(p => p.is_admin !== 1);
      
      setAdminPermissions(adminPerms);
      setRegularPermissions(regularPerms);
    } catch (error) {
      notification.error({
        message: 'Error fetching permissions',
        description: error.message || 'Failed to load permissions'
      });
    }
  };

  // Show confirm dialog before deleting
  const showDeleteConfirm = (roleId, roleName) => {
    confirm({
      title: 'Are you sure you want to delete this role?',
      content: `You are about to delete the role "${roleName}". This action cannot be undone and may affect users with this role.`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(roleId);
      }
    });
  };

  // Handle delete role
  const handleDelete = async (roleId) => {
    try {
      await roleService.deleteRole(roleId);
      notification.success({
        message: 'Role deleted successfully'
      });
      fetchRoles();
    } catch (error) {
      notification.error({
        message: 'Error deleting role',
        description: error.message || 'Failed to delete role'
      });
    }
  };

  // Show modal for add/edit
  const showModal = (role = null) => {
    setEditingRole(role);
    
    if (role) {
      form.setFieldsValue({
        name: role.name,
        description: role.description,
        display_name: role.display_name || '',
        is_admin: role.is_admin === 1
      });
    } else {
      form.resetFields();
      // Default is non-admin role
      form.setFieldsValue({
        is_admin: false
      });
    }
    
    setModalVisible(true);
  };

  // Show permissions modal
  const showPermissionsModal = (role) => {
    setEditingRole(role);
    setSelectedPermissions(role.permissions?.map(p => p.id) || []);
    setPermissionsModalVisible(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Convert is_admin boolean to 0 or 1
      values.is_admin = values.is_admin ? 1 : 0;
      
      if (editingRole) {
        // Update existing role
        await roleService.updateRole(editingRole.id, values);
        notification.success({
          message: 'Role updated successfully'
        });
      } else {
        // Create new role
        await roleService.createRole(values);
        notification.success({
          message: 'Role created successfully'
        });
      }
      
      setModalVisible(false);
      fetchRoles();
    } catch (error) {
      notification.error({
        message: 'Error saving role',
        description: error.message || 'Failed to save role'
      });
    }
  };

  // Handle permission assignment
  const handlePermissionAssignment = async () => {
    try {
      if (!editingRole) return;
      
      // Check if the role is admin and permissions are valid
      if (editingRole.is_admin === 1) {
        // For admin roles, validate that only admin permissions are selected
        const invalidPermissions = selectedPermissions.filter(permId => {
          const permission = permissions.find(p => p.id === permId);
          return permission && permission.is_admin !== 1;
        });
        
        if (invalidPermissions.length > 1000) {
          notification.warning({
            message: 'Invalid Permissions',
            description: 'Admin roles can only have admin permissions. Please adjust your selection.'
          });
          return;
        }
      } else {
        // For regular roles, validate that no admin permissions are selected
        const invalidPermissions = selectedPermissions.filter(permId => {
          const permission = permissions.find(p => p.id === permId);
          return permission && permission.is_admin === 1;
        });
        
        if (invalidPermissions.length > 1000) {
          notification.warning({
            message: 'Invalid Permissions',
            description: 'Regular roles cannot have admin permissions. Please adjust your selection.'
          });
          return;
        }
      }
      if(editingRole.is_admin === 1) {
      await roleService.syncPermissions(editingRole.id, selectedPermissions.filter(permId => {
        const permission = permissions.find(p => p.id === permId);
        return permission && permission.is_admin === 1;
      }));
      } else {
        await roleService.syncPermissions(editingRole.id, selectedPermissions.filter(permId => {
          const permission = permissions.find(p => p.id === permId);
          return permission && permission.is_admin !== 1;
        }));
      }
      
      notification.success({
        message: 'Permissions updated successfully',
        description: `Permissions for role "${editingRole.name}" have been updated.`
      });
      
      setPermissionsModalVisible(false);
      fetchRoles();
    } catch (error) {
      notification.error({
        message: 'Error updating permissions',
        description: error.message || 'Failed to update permissions'
      });
    }
  };

  // Handle transfer change
  const handleTransferChange = (nextTargetKeys) => {
    setSelectedPermissions(nextTargetKeys);
  };

  // Filter permissions based on role type
  const getPermissionsForRole = (roleIsAdmin) => {
    if (roleIsAdmin) {
      return permissions.filter(p => p.is_admin === 1);
    } else {
      return permissions.filter(p => p.is_admin !== 1);
    }
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: (a, b) => (a.display_name || '').localeCompare(b.display_name || '')
    },
    {
      title: 'Type',
      key: 'is_admin',
      render: (_, record) => (
        record.is_admin === 1 ? (
          <Tag color="red" icon={<AlertFilled />}>Admin</Tag>
        ) : (
          <Tag color="blue" icon={<LockOutlined />}>Regular</Tag>
        )
      ),
      filters: [
        { text: 'Admin', value: 1 },
        { text: 'Regular', value: 0 }
      ],
      onFilter: (value, record) => record.is_admin === value
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (_, record) => (
        <div>
          {record.permissions?.length ? (
            <div>
              {record.permissions.slice(0, 3).map(perm => (
                <Tag key={perm.id} color={perm.is_admin === 1 ? 'red' : 'blue'}>
                  {perm.name}
                </Tag>
              ))}
              {record.permissions.length > 3 && (
                <Tag color="default">+{record.permissions.length - 3} more</Tag>
              )}
            </div>
          ) : (
            <span className="text-gray-400">No permissions</span>
          )}
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Role">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => showModal(record)}
              disabled={!canManageRoles}
              className="text-blue-500 hover:text-blue-700"
            />
          </Tooltip>
          <Tooltip title="Manage Permissions">
            <Button 
              type="text" 
              icon={<SettingOutlined />} 
              onClick={() => showPermissionsModal(record)}
              disabled={!canManageRoles}
              className="text-green-500 hover:text-green-700"
            />
          </Tooltip>
          <Tooltip title="Delete Role">
            <Popconfirm
              title="Delete this role?"
              onConfirm={() => showDeleteConfirm(record.id, record.name)}
              okText="Yes"
              cancelText="No"
              disabled={!canManageRoles}
            >
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
                disabled={!canManageRoles}
                className="text-red-500 hover:text-red-700"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  // Render component
  return (
    <div className="p-6">
      <Card className="shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Roles Management</h1>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => showModal()}
            disabled={!canManageRoles}
            className="bg-golden-navy"
          >
            Add Role
          </Button>
        </div>

        <div className="mb-4">
          <Tabs 
            activeKey={tabActiveKey} 
            onChange={setTabActiveKey}
            type="card"
          >
            <TabPane 
              tab={
                <span>
                  All Roles
                  <Tag className="ml-2" color="default">{roles.length}</Tag>
                </span>
              }
              key="all"
            />
            <TabPane 
              tab={
                <span>
                  <AlertFilled /> Admin Roles
                  <Tag className="ml-2" color="red">
                    {roles.filter(r => r.is_admin === 1).length}
                  </Tag>
                </span>
              }
              key="admin"
            />
            <TabPane 
              tab={
                <span>
                  <LockOutlined /> Regular Roles
                  <Tag className="ml-2" color="blue">
                    {roles.filter(r => r.is_admin !== 1).length}
                  </Tag>
                </span>
              }
              key="regular"
            />
          </Tabs>
        </div>

        <div className="mb-4 flex justify-between items-center">
          <Search
            placeholder="Search roles..."
            allowClear
            onSearch={value => setSearchText(value)}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchRoles}
          >
            Refresh
          </Button>
        </div>

        <MyTable
          rowKey="id"
          columns={columns}
          dataSource={filteredRoles}
          loading={loading}
        
          bordered
          rowClassName={record => record.is_admin === 1 ? 'bg-red-50' : ''}
        />
      </Card>

      {/* Add/Edit Role Modal */}
      <Modal
        title={editingRole ? 'Edit Role' : 'Add Role'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleSubmit}
            className="bg-golden-navy"
          >
            {editingRole ? 'Update' : 'Create'}
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Role Name"
            rules={[
              { required: true, message: 'Please enter a name' },
              { 
                pattern: /^[a-z_]+$/, 
                message: 'Name must contain only lowercase letters and underscores' 
              }
            ]}
          >
            <Input placeholder="E.g., admin, manager, editor" />
          </Form.Item>
          
          <Form.Item
            name="display_name"
            label="Display Name"
            tooltip="Human-readable name shown in the UI"
          >
            <Input placeholder="E.g., Administrator, Manager, Editor" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea 
              placeholder="Describe the role and its responsibilities" 
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="is_admin"
            label="Admin Role"
            valuePropName="checked"
            tooltip="Admin roles provide access to admin-only features and permissions"
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          
          <Alert
            type="info"
            showIcon
            message="Role Type Information"
            description={
              <ul className="mt-2 ml-2">
                <li><strong>Admin Roles:</strong> Can access admin features and can only be assigned admin permissions.</li>
                <li><strong>Regular Roles:</strong> For regular users and can only be assigned regular permissions.</li>
              </ul>
            }
          />
        </Form>
      </Modal>

      {/* Permissions Modal */}
      <Modal
        title={`Manage Permissions for "${editingRole?.name || ''}"`}
        width={800}
        open={permissionsModalVisible}
        onCancel={() => setPermissionsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setPermissionsModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handlePermissionAssignment}
            className="bg-golden-navy"
          >
            Save Permissions
          </Button>
        ]}
      >
        <div className="mb-4">
          <Alert
            type={editingRole?.is_admin === 1 ? "warning" : "info"}
            showIcon
            message={
              editingRole?.is_admin === 1 
                ? "Admin Role Permission Assignment"
                : "Regular Role Permission Assignment"
            }
            description={
              editingRole?.is_admin === 1
                ? "This is an admin role. You can only assign admin permissions to it."
                : "This is a regular role. You can only assign regular (non-admin) permissions to it."
            }
            className="mb-4"
          />
          <p>
            Select the permissions to assign to this role. Users with this role will have all selected permissions.
          </p>
        </div>
        
        <Transfer
          dataSource={
            // Filter permissions based on role type
            (editingRole?.is_admin === 1 ? adminPermissions : regularPermissions).map(perm => ({
              key: perm.id,
              title: perm.name,
              description: perm.description,
              group: perm.group || 'General',
              is_admin: perm.is_admin
            }))
          }
          titles={['Available', 'Assigned']}
          targetKeys={selectedPermissions}
          onChange={handleTransferChange}
          render={item => (
            <div>
              <div className="font-medium">
                {item.is_admin === 1 && <AlertFilled className="mr-1 text-red-500" />}
                {item.title}
              </div>
              <div className="text-xs text-gray-500 truncate" title={item.description}>
                {item.description}
              </div>
              {item.group && (
                <Tag color={item.is_admin === 1 ? "red" : "blue"} className="mt-1">
                  {item.group}
                </Tag>
              )}
            </div>
          )}
          listStyle={{
            width: 350,
            height: 500,
          }}
          operations={['→', '←']}
          showSearch
          filterOption={(inputValue, item) =>
            item.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
            item.description?.toLowerCase().includes(inputValue.toLowerCase()) 
          }
        />
      </Modal>
    </div>
  );
};

export default RoleList;